import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay } from 'rxjs/operators';
import moment from 'moment';
import { HolidayService } from '@intranet/modules/holidays/holiday.service';
import { AvailabilityIndicator, UserAvailabilityDay } from '@intranet/modules/holidays/holiday.types';

export interface UserAvailabilitySummary {
    // Count of weekdays (weekends excluded), from today through the end of
    // next week, this person isn't fully available. This is the only number
    // the avatar badge ever shows. That window is capped so this can never
    // exceed 10 (today being a Monday is the longest case: two full 5-day
    // working weeks).
    awayDays: number;
    // Whether today itself (a weekday) is one of those days — used to give
    // an away-right-now badge more visual weight than an away-later one.
    awayToday: boolean;
    // One entry per away day (weekends excluded), for the tooltip's "Away:"
    // list — e.g. "Today", "Tomorrow", "Monday 7 September", optionally
    // suffixed " am"/" pm" for a half day. Empty when fully available, so
    // the tooltip shows nothing beyond the person's name.
    awayLines: string[];
}

@Injectable({ providedIn: 'root' })
export class AvailabilityService {
    private summaries$: Observable<Map<string, UserAvailabilitySummary>> | null = null;

    constructor(private holidayService: HolidayService) {}

    // Fetched once and shared across every avatar on the page, not once per
    // avatar instance — a table with 50 rows still makes a single request.
    getSummaries(): Observable<Map<string, UserAvailabilitySummary>> {
        if (!this.summaries$) {
            this.summaries$ = this.holidayService.getAllUserAvailability().pipe(
                map(result => {
                    const summaries = new Map<string, UserAvailabilitySummary>();
                    if (result && result.success && result.users) {
                        result.users.forEach(u => {
                            summaries.set(u.user.id, this.summarize(u.days));
                        });
                    }
                    return summaries;
                }),
                catchError(() => of(new Map<string, UserAvailabilitySummary>())),
                shareReplay({ bufferSize: 1, refCount: false })
            );
        }
        return this.summaries$;
    }

    private isWeekend(dateStr: string): boolean {
        const dow = moment.parseZone(dateStr).day();
        return dow === 0 || dow === 6;
    }

    private dayLabel(dateStr: string, offset: number): string {
        if (offset === 0) return 'Today';
        if (offset === 1) return 'Tomorrow';
        return moment.parseZone(dateStr).format('ddd D MMM');
    }

    private summarize(days: UserAvailabilityDay[]): UserAvailabilitySummary {
        const awayDays = days.filter(
            d => !this.isWeekend(d.date) && d.availability !== AvailabilityIndicator.AllDay
        ).length;
        const awayToday = !this.isWeekend(days[0].date) && days[0].availability !== AvailabilityIndicator.AllDay;
        return { awayDays, awayToday, awayLines: this.buildAwayLines(days) };
    }

    // One line per away weekday — no grouping into ranges, just the plain
    // list the tooltip's "Away:" column shows.
    private buildAwayLines(days: UserAvailabilityDay[]): string[] {
        const lines: string[] = [];
        days.forEach((day, i) => {
            if (this.isWeekend(day.date) || day.availability === AvailabilityIndicator.AllDay) return;

            // The enum says when the person IS available, not which half is
            // booked off (see AvailabilityIndicator) — Morning means
            // available in the morning, so away in the afternoon ("pm"), and
            // Afternoon means the opposite ("am"). Deliberately inverted.
            const suffix = day.availability === AvailabilityIndicator.Morning ? ' pm'
                : day.availability === AvailabilityIndicator.Afternoon ? ' am'
                : '';

            lines.push(this.dayLabel(day.date, i) + suffix);
        });
        return lines;
    }
}
